// Client-side BCH signed-message signer. Mirrors the server's
// `billing-service/src/auth-signature.ts` verify path 1:1 — same
// canonical request line, same dsha256 prefix, same 65-byte
// recoverable wire format. Importing both files into a shared
// package would be cleanest; for now they're held in sync by
// inspection (both sides of the contract are well-tested).

import {
  binToBase64,
  binToHex,
  secp256k1,
  sha256,
  utf8ToBin,
} from '@bitauth/libauth';

const MESSAGE_PREFIX = utf8ToBin('\x18Bitcoin Signed Message:\n');

export interface SignedHeaders {
  'X-Solidpeer-Account':   string;
  'X-Solidpeer-Timestamp': string;
  'X-Solidpeer-Signature': string;
}

/**
 * Sign a request and produce the three SolidPeer auth headers. Caller
 * stamps them onto the outbound `fetch()` alongside `Content-Type`.
 *
 * `body` must be the EXACT body bytes that will be sent on the wire —
 * the server hashes the same body, so any pre-flight transformation
 * (JSON.stringify, formatting, etc.) needs to happen here, not after.
 */
export function signRequest(args: {
  privkey:    Uint8Array;
  account_id: string;
  method:     string;
  path:       string;
  body:       string;
}): SignedHeaders {
  const ts      = Math.floor(Date.now() / 1000).toString();
  const message = canonicalRequestMessage(args.method, args.path, ts, args.body);
  const hash    = bchSignedMessageHash(message);

  const sig = secp256k1.signMessageHashRecoverableCompact(args.privkey, hash);
  if (typeof sig === 'string') throw new Error(`sign failed: ${sig}`);

  // BCH-wallet wire format: header byte = 27 + 4(compressed) + recoveryId,
  // followed by 64 bytes of compact r||s.
  const wire = new Uint8Array(65);
  wire[0]   = 27 + 4 + sig.recoveryId;
  wire.set(sig.signature, 1);

  return {
    'X-Solidpeer-Account':   args.account_id,
    'X-Solidpeer-Timestamp': ts,
    'X-Solidpeer-Signature': binToBase64(wire),
  };
}

// ============================================================
// Internals — kept identical to billing-service/src/auth-signature.ts
// ============================================================

function canonicalRequestMessage(
  method: string, path: string, timestamp: string, body: string,
): string {
  const body_hash_hex = binToHex(sha256.hash(utf8ToBin(body)));
  return `${method}\n${path}\n${timestamp}\n${body_hash_hex}`;
}

function bchSignedMessageHash(message: string): Uint8Array {
  const bin    = utf8ToBin(message);
  const varint = encodeVarint(bin.length);
  const concat = new Uint8Array(MESSAGE_PREFIX.length + varint.length + bin.length);
  concat.set(MESSAGE_PREFIX, 0);
  concat.set(varint, MESSAGE_PREFIX.length);
  concat.set(bin, MESSAGE_PREFIX.length + varint.length);
  return sha256.hash(sha256.hash(concat));
}

function encodeVarint(n: number): Uint8Array {
  if (n < 0xfd)        return new Uint8Array([n]);
  if (n <= 0xffff)     return new Uint8Array([0xfd, n & 0xff, (n >> 8) & 0xff]);
  if (n <= 0xffffffff) {
    return new Uint8Array([
      0xfe, n & 0xff, (n >> 8) & 0xff, (n >> 16) & 0xff, (n >> 24) & 0xff,
    ]);
  }
  throw new Error('varint too large for HTTP request body');
}
